export const ORGANIZATIONS_CLAIM = 'urn:elsevier:identity:organizations';

export interface IOrganization {
    id: string;
    name: string;
}

export interface IParsedOrganizations {
    organizations: IOrganization[];
    isMalformed: boolean;
}

const isValidOrganization = (entry: any): entry is IOrganization =>
    !!entry &&
    typeof entry === 'object' &&
    (typeof entry.id === 'string' || typeof entry.id === 'number') &&
    String(entry.id).trim() !== '' &&
    typeof entry.name === 'string' &&
    entry.name.trim() !== '';

/**
 * Reads the NeoID organizations claim defensively. A missing/empty claim yields
 * no organizations; any other unexpected shape yields no organizations and is
 * flagged as malformed so the caller can log it.
 */
export function parseOrganizationsClaim(claim: unknown): IParsedOrganizations {
    if (claim === undefined || claim === null) {
        return { organizations: [], isMalformed: false };
    }
    if (!Array.isArray(claim)) {
        return { organizations: [], isMalformed: true };
    }
    if (!claim.every(isValidOrganization)) {
        return { organizations: [], isMalformed: true };
    }
    return {
        organizations: claim.map(({ id, name }) => ({
            id: String(id),
            name: name.trim(),
        })),
        isMalformed: false,
    };
}
// The organization the user is currently working in: the claim entry matching
// the current ECR id, or the sole entry for a single-organization user. An
// unmatched multi-organization claim returns null rather than guessing, so the
// header never names a location that is not known to be active.
export const getActiveOrganization = (
    organizations: IOrganization[],
    ecrId?: string | number | null
): IOrganization | null =>
    organizations.find(({ id }) => ecrId != null && id === String(ecrId)) ??
    (organizations.length === 1 ? organizations[0] : null);

// "Change location" is only offered when the user belongs to 2+ organizations
// (and the account-center feature flag is on); the picker itself is NeoID's.
export const shouldShowChangeLocation = (
    isFeatureEnabled: boolean,
    organizations: IOrganization[]
): boolean => isFeatureEnabled && organizations.length > 1;
